<template>
    <div class="card">
        <!-- Header -->
        <div v-if="isHeaderEnabled" class="card-header p-3">
            <div class="row justify-content-between flex-grow-1">
                <div class="col" v-if="searchingEnabled">
                    <form autocomplete="off" @submit.prevent="performSearch">
                        <!-- Search -->
                        <div class="input-group input-group-merge datatable-search">
                            <div class="input-group-prepend input-group-text">
                                <i class="bi-search"></i>
                            </div>
                            <input
                                name="search-term"
                                type="text"
                                class="form-control form-control-sm"
                                :placeholder="$t('Enter search term')"
                                :data-hs-form-search-options=searchOptions
                                :id="idSearchInput"
                                v-model="searchTerm"
                            >
                            <AppButton
                                type="submit"
                                btnClass="input-group-append input-group-text"
                            >
                                <i
                                    :id="idSearchClearIcon"
                                    class="bi-x-lg"
                                    style="display: none; margin-right: 5em;"
                                />
                            </AppButton>
                            <AppButton
                                type="submit"
                                btnClass="btn-white btn-sm"
                            >
                                {{ $t('Search') }}
                            </AppButton>
                        </div>
                        <!-- End Search -->
                    </form>
                </div>

                <div class="col">
                    <slot name="quickFilters" />
                </div>

                <div class="col-auto" v-if="showFiltersAndActions">
                    <!-- Filter -->
                    <div class="row">
                        <slot name="filters" />
                        <template v-if="hideTableActions">
                            <div class="col-auto">
                                <AppDropdown class="pt-2">
                                <template #toggler="{ toggle }">
                                    <a href="#"
                                       @click="toggle"
                                    >
                                        <i class="bi bi-three-dots-vertical"></i>
                                    </a>
                                </template>
                                <AppDropdownContent class="fixed-block">
                                    <AppDropdownItem>
                                        <ColumnSelector
                                            btnClass=""
                                            :columns="columns"
                                            :icon-format="false"
                                            @set-visibility="($e, col) => col.visible = $e.target.checked"
                                        />
                                    </AppDropdownItem>
                                    <AppDropdownItem>
                                        <AppButton
                                            btnClass="dropdown-toggle"
                                            type="button"
                                            :id="idPageLength"
                                            data-bs-toggle="dropdown"
                                            aria-expanded="false"
                                        >
                                            {{ $t('Page length') }}
                                            {{ pageLength }}
                                        </AppButton>
                                        <ul class="dropdown-menu" :aria-labelledby="idPageLength">
                                            <li v-for="option in [5, 15, 30, 50, 100, 500]" :key="option">
                                                <a
                                                    class="dropdown-item"
                                                    href="#"
                                                    @click="updatePageLength(option)"
                                                >{{ option }}</a>
                                            </li>
                                        </ul>
                                    </AppDropdownItem>
                                    <AppDropdownItem v-if="props.showExportCsv">
                                        <AppButton
                                            type="button"
                                            btnClass=""
                                            @click="exportCsv"
                                            :is-loading="isCsvExport"
                                            data-bs-toggle="tooltip"
                                            data-bs-html="true"
                                            :title="$t('Export to CSV')"
                                        >
                                            {{ $t('Export to CSV') }}
                                        </AppButton>
                                    </AppDropdownItem>
                                    <AppDropdownItem v-if="props.showDateChangeUtility">
                                        <div v-if="auth_user && hasPermission(auth_user.can, 'update.machine.date_change_utility')">
                                            <AppButton type="button"
                                                       btnClass=""
                                                       data-bs-toggle="tooltip"
                                                       data-bs-html="true"
                                                       :title="$t('Date Change Utility')"
                                                       @click="toggleModalChangeUtility">
                                                {{ $t('Date Change Utility') }}
                                            </AppButton>
                                        </div>
                                    </AppDropdownItem>
                                </AppDropdownContent>
                            </AppDropdown>
                                <div v-if="props.showDateChangeUtility">
                                    <DateChangeUtility :device="device" :isOpenModal="isOpenModalChangeUtility" @update:is-open-modal="toggleModalChangeUtility" />
                                </div>
                            </div>
                        </template>
                        <template v-else>
                            <div class="col-auto">
                                <ColumnSelector
                                    :columns="columns"
                                    @set-visibility="($e, col) => col.visible = $e.target.checked"
                                />
                            </div>
                            <div class="col-auto">
                                <div class="dropdown">
                                    <AppButton
                                        btnClass="btn-white btn-sm dropdown-toggle"
                                        type="button"
                                        :id="idPageLength"
                                        data-bs-toggle="dropdown"
                                        aria-expanded="false"
                                    >
                                        <i class="bi bi-list-ol me-1"></i>
                                        {{ pageLength }}
                                    </AppButton>
                                    <ul class="dropdown-menu" :aria-labelledby="idPageLength">
                                        <li v-for="option in [5, 15, 30, 50, 100, 500]" :key="option">
                                            <a
                                                class="dropdown-item"
                                                href="#"
                                                @click="updatePageLength(option)"
                                            >{{ option }}</a>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <div class="col-auto" v-if="props.showExportCsv">
                                <AppButton
                                    type="button"
                                    btnClass="btn-white btn-sm"
                                    @click="exportCsv"
                                    :is-loading="isCsvExport"
                                    data-bs-toggle="tooltip"
                                    data-bs-html="true"
                                    :title="$t('Export to CSV')"
                                >
                                    <i class="bi bi-filetype-csv"></i>
                                </AppButton>
                            </div>
                        </template>
                    </div>
                    <!-- End Filter -->
                </div>

                <div class="row mt-1">
                    <slot name="selectedFilters"></slot>
                </div>
            </div>
        </div>
        <!-- End Header -->
        <div class="table-responsive">
            <table
                :id="props.id"
                style="width: 100%;"
                class="table table-sm table-hover table-thead-bordered table-nowrap table-align-middle"
            >
                <thead class="thead-light sticky-header">
                <tr>
                    <th
                        v-for="col in visibleColumns"
                        :key="col.name"
                        v-show="col.visible"
                        @click="changeOrder($event, col)"
                        :class="{
                            'header-filter-container': col.inputFilter,
                            'sortable': col.sortable,
                            'sorting-asc': orderDir === 'asc' && orderBy === col.name,
                            'sorting-desc': orderDir === 'desc' && orderBy === col.name
                        }"
                        :style="{ width: col.width ? col.width : 'auto' }"
                        >
                            <template v-if="col.title">
                                {{ col.title }}
                            </template>
                            <template v-else-if="col.html">
                                <div v-html="col.html"></div>
                            </template>
                            <template v-else-if="col.icon">
                                <i :class="col.icon"></i>
                            </template>
                            <template v-if="col.checkbox">
                                <input
                                    type="checkbox"
                                    id="formCheckAll"
                                    class="form-check-input"
                                    v-model="selectAllChecked"
                                >
                            </template>
                            <template v-else-if="col.inputFilter">
                                <div class="input-group">
                                    <input
                                        type="text"
                                        class="form-control form-control-sm data-table-header-filter border-end-0 ms-1"
                                        :value="col.inputFilter.defaultValue"
                                        :placeholder="col.inputFilter.placeholder"
                                        @keyup="col.inputFilter.onKeyupHandler"
                                    >
                                    <span @click="col.inputFilter.clearHandler()" class="input-group-text bg-transparent input-filter-icon">
                                            X
                                        </span>
                                </div>
                            </template>

                            <i
                                v-if="col.sortable && orderBy === col.name && orderDir === 'asc'"
                                class="bi bi-arrow-up ms-1"
                            />
                            <i
                                v-else-if="col.sortable && orderBy === col.name && orderDir === 'desc'"
                                class="bi bi-arrow-down ms-1"
                            />
                            <i
                                v-else-if="col.sortable && orderBy !== col.name"
                                class="bi bi-arrow-down-up ms-1"
                            />

                            <template v-if="col.info">
                                <AppIcon
                                    :icon="faQuestionCircle"
                                    :tooltip="{ content: col.info, allowHTML: true }"
                                    class="ms-1"
                                />
                            </template>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-if="!tableData?.data || tableData.data.length === 0"
                        style="height: 80px"
                    >
                        <td :colspan="visibleColumns.length">
                            <div
                                class="text-center p-4"
                                style="position: fixed; left: 50vw; transform: translate(-50%, -50%);"
                            >
                                <p class="mb-0">{{ $t('No data to show') }}</p>
                            </div>
                        </td>
                    </tr>
                    <tr
                        v-else
                        v-for="item in tableData.data"
                        :key="item.id"
                        @click="enableRowClick && handleRowClick(item)"
                        :class="{ 'clickable-row': enableRowClick }"
                    >
                        <!-- Render table cells based on columns configuration -->
                        <td
                            v-for="col in visibleColumns"
                            :key="col.name"
                            v-show="col.visible"
                            :class="col.className"
                            @click="col.onClick ? col.onClick(item[col.name], item) : callParentModal(col.openModal, item)"
                        >
                            <template v-if="typeof col.render === 'function'">
                                <template v-if="col.tooltip">
                                    <span
                                        v-html="col.render(item[col.name], item)"
                                        v-tooltip="typeof col.tooltip === 'function' ? col.tooltip(item[col.name], item) : col.tooltip"
                                    />
                                </template>
                                <template v-else-if="col.pre">
                                    <pre v-html="col.render(item[col.name], item)" />
                                </template>
                                <template v-else>
                                    <span v-html="col.render(item[col.name], item)" />
                                </template>
                            </template>
                            <template v-else-if="typeof col.loadModal === 'function'">
                                <span
                                    v-html="col.loadModal(item[col.name], item)"
                                    @click="loadModalFromParentComponent(item)"
                                />
                            </template>
                            <span v-else-if="col.component">
                                <component
                                    v-if="col.component"
                                    :is="col.component"
                                    v-bind="col.props"
                                    :data="item"
                                />
                            </span>
                            <span v-else>
                                {{ item[col.name] }}
                            </span>
                            <template v-if="typeof col.faicon === 'function' && col.faicon(item[col.name], item)">
                                <span
                                    v-if="col.tooltip"
                                    v-tooltip="typeof col.tooltip === 'function' ? col.tooltip(item[col.name], item) : col.tooltip"
                                    class="ms-2 fs-4"
                                >
                                    <font-awesome-icon
                                        :icon="col.faicon(item[col.name], item)"
                                    />
                                </span>
                                <span v-else class="ms-2 fs-4">
                                    <font-awesome-icon
                                        :icon="col.faicon(item[col.name], item)"
                                    />
                                </span>
                            </template>
                        </td>
                    </tr>
                </tbody>
            </table>
            <!-- Loading spinner overlay -->
            <div v-if="isLoading && loaderEnabled" class="loading-overlay">
                <AppLoading :loading="isLoading" />
            </div>

        </div>
        <!-- Footer -->
        <div class="card-footer p-3">
            <template v-if="!props.useSimplePagination">
                <div class="row justify-content-between align-items-center">
                    <!-- Left column for table info -->
                    <div class="col-md-6">
                        {{ $t('Showing') }} {{ tableData.from || 0 }} {{ $t('to') }} {{ tableData.to || 0 }} {{ $t('of') }} {{ tableData.total || 0 }}
                        {{ $t('entries') }}
                    </div>

                    <!-- Right column for pagination controls -->
                    <div class="col-md-6 mt-3 mt-md-0">
                        <div class="float-end">
                            <Pagination
                                :data="tableData"
                                @pagination-change-page="changePage"
                                :keepLength="false"
                                :limit="5"
                            />
                        </div>
                    </div>
                </div>
            </template>
            <template v-else>
                <div class="row justify-content-between align-items-center">
                    <div class="col-md-6">
                        {{ $t('Showing') }} {{ tableData.from || 0 }} {{ $t('to') }} {{ tableData.to || 0 }}
                    </div>

                    <div class="col-md-6 mt-3 mt-md-0">
                        <ul class="pagination justify-content-end">
                            <li
                                class="page-item pagination-prev-nav"
                                v-if="tableData.prev_page_url"
                            >
                                <a
                                    class="page-link"
                                    href="#"
                                    aria-label="Previous"
                                    @click="changePage(page - 1)"
                                >
                                    <span aria-hidden="true">&laquo; Prev</span>
                                </a>
                            </li>

                            <li
                                class="page-item pagination-next-nav"
                                v-if="tableData.next_page_url || tableData.next_token"
                            >
                                <a
                                    class="page-link"
                                    href="#"
                                    aria-label="Next"
                                    @click="changePage(page + 1)"
                                >
                                    <span aria-hidden="true">Next &raquo;</span>
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>
            </template>
        </div>
        <!-- End Footer -->
    </div>
</template>

<script setup>
import ColumnSelector from "./ColumnSelector.vue";
import {computed, defineEmits, onMounted, ref, shallowRef, watch} from "vue";
import {startCase} from "../helpers";
import AppButton from "@/vue/shared/AppButton.vue";
import AppDropdown from "@/vue/shared/AppDropdown.vue";
import AppDropdownContent from "@/vue/shared/AppDropdownContent.vue";
import AppDropdownItem from "@/vue/shared/AppDropdownItem.vue";
import {Bootstrap5Pagination as Pagination} from "laravel-vue-pagination";
import moment from "moment";
import {createConfirmDialog} from "vuejs-confirm-dialog";
import AppAlert from "@/vue/shared/dialog/AppAlert.vue";
import AppIcon from "@/vue/shared/AppIcon.vue";
import {faQuestionCircle} from "@fortawesome/free-regular-svg-icons";
import {faEraser} from "@fortawesome/free-solid-svg-icons";
import DateChangeUtility from "@/vue/connect/devices/tabs/usage/DateChangeUtility.vue";
import AppLoading from "@/vue/shared/AppLoading.vue";
import {useUserStore} from "@/vue/store/userStore";
import usePermissions from "@/vue/composables/usePermissions";

const { reveal: alert } = createConfirmDialog(AppAlert);

const emit = defineEmits([
    "row-click",
    "call-parent-modal",
    "selectAllChange",
    "pageLoaded",
    "fetchingData",
    "update:modelValue",
    "update:order",
    "serverError",
    "refreshTableData"
]);

const props = defineProps({
    id: {
        type: String
    },
    url: {
        type: String
    },
    columns: {
        type: Array
    },
    filters: {
        type: Object
    },
    lazyLoad: {
        type: Boolean,
        default: false
    },
    refresh: {
        type: [Boolean, Number],
        default: false
    },
    refreshSilent: {
        type: [Boolean, Number],
        default: false
    },
    searchEmpty: {
        type: Boolean,
        default: false
    },
    useLocalStorage: {
        type: Boolean,
        default: false
    },
    searchingEnabled: {
        type: Boolean,
        default: true
    },
    pageLength: {
        type: Number,
        default: 50
    },
    orderBy: {
        type: String,
        default: null
    },
    orderDir: {
        type: String,
        default: null
    },
    showExportCsv: {
        type: Boolean,
        default: false
    },
    exportCsvCallback: {
        type: Function,
        required: false,
        default: null
    },
    showDateChangeUtility: {
        type: Boolean,
        default: false
    },
    device: {
        type: [Object, null],
        default: null
    },
    headerEnabled: {
        type: Boolean,
        default: true
    },
    hideTableActions: {
        type: Boolean,
        default: false
    },
    showFiltersAndActions: {
        type: Boolean,
        default: true
    },
    modelValue: {
        type: Array,
        required: false,
    },
    loaderEnabled: {
        type: Boolean,
        default: true
    },
    hideEmptyColumns: {
        type: Boolean,
        default: false
    },
    useSimplePagination: {
        type: Boolean,
        default: false
    },
    enableRowClick: {
        type: Boolean,
        default: false
    },
    displayErrorAlert: {
        type: Boolean,
        default: true
    },
});



const columns = ref(props.columns);
const idPageLength = `${props.id}PageLength`;
const idLocalStorage = `${props.id}LocalStorage`;
const idSearchInput = `${props.id}SearchInput`;
const idSearchClearIcon = `${props.id}SearchClearIcon`;
const searchOptions = `{"clearIcon": "#${idSearchClearIcon}"}`;
const isHeaderEnabled = ref(props.headerEnabled)
const abortController = ref(null);

const tableData = ref({
    data: []
});

const userStore = useUserStore();
const auth_user = computed(() => userStore.user);
const { hasPermission } = usePermissions();

const page = ref(1);
const pageLength = ref(props.pageLength);
const orderBy = ref(props.orderBy);
const orderDir = ref(props.orderDir || "asc");
const searchTerm = ref("");
const isCsvExport = ref(false);
const isOpenModalChangeUtility = ref(false);

const selectAllChecked = ref(false);

const isLoading = ref(false);
const clickCounts = ref({});

const lastRequestTimestamp = ref(0);

const handleRowClick = (item) => {
    emit("row-click", item);
};

// @deprecated
const loadModalFromParentComponent = (rowContext) => {
    emit("call-parent-modal", rowContext);
};

function toggleModalChangeUtility() {
    isOpenModalChangeUtility.value = !isOpenModalChangeUtility.value;
}

const callParentModal = (modalReference, rowContext) => {
    if (!modalReference) {
        return;
    }

    emit("call-parent-modal", modalReference, rowContext);
};

const visibleColumns = computed(() => {
    if (props.hideEmptyColumns) {
        // Hide columns that are entirely null if hideEmptyColumns is true
        return columns.value.filter(col => col.visible && hasDataForColumn(col));
    } else {
        // If hideEmptyColumns is false
        return columns.value.filter(col => col.visible);
    }
});
function hasDataForColumn(col) {
    // If the column has a render function, always show it
    if (typeof col.render === 'function' && !col?.hideEmpty) {
        return true;
    }

    return tableData.value.data.some(item => {
        const value = item[col.name];
        return value !== undefined && value !== null;
    });
}


const hasFilters = (filters) => {
    if (!filters) return false;
    return Object.values(filters).some((value) => {
        if (Array.isArray(value)) {
            return value.length > 0;
        } else if (typeof value === "object") {
            return Object.values(value).every((v) => v);
        } else {
            return value;
        }
    });
};
const shouldFetch = computed(() => {
    // If the 'lazyLoad' prop is set to false, we should fetch data always
    if (props.lazyLoad === false) {
        return true;
    }

    // We will allow fetching data for empty terms if "searchEmpty" property is set.
    if (searchTerm.value === "" && props.searchEmpty) {
        return true;
    }

    // If there is no search term entered by the user or any filters applied, we should not fetch data
    if (searchTerm.value === "" && !hasFilters(props.filters)) {
        return false;
    }

    // After all, if there are filters or a search term is present, we should fetch data
    return true;
});

const runExportCsvCallback = async function() {
    if (typeof props.exportCsvCallback !== 'function') {
        return true;
    }

    try {
        return await props.exportCsvCallback();
    } catch (error) {
        console.error("Error in exportCsvCallback:", error);

        return false;
    }
}

const exportCsv = async function() {
    isCsvExport.value = true;

    let shouldFetch = await runExportCsvCallback();

    if (shouldFetch) {
        await fetchData();
    }

    isCsvExport.value = false;
};

const buildParams = function() {
    const params = new URLSearchParams();
    params.append("page", page.value);
    params.append("limit", pageLength.value);

    if (orderBy.value && orderDir.value) {
        params.append("orderBy", orderBy.value);
        params.append("orderDir", orderDir.value);
    }

    if (searchTerm.value) {
        params.append("searchTerm", searchTerm.value);
    }

    if (tableData.value?.next_token) {
        params.append("next", tableData.value?.next_token);
    }

    if (props.filters) {
        Object.entries(props.filters).forEach(([key, value]) => {
            if (value === "") {
                return;
            }

            if (typeof value === "object") {
                Object.entries(value).forEach(([k, v]) => {
                    if (v === "") {
                        return;
                    }

                    params.append(`${key}[${k}]`, v?.toString());
                });
            } else if (typeof value !== "undefined") {
                params.append(key, value.toString());
            }
        });
    }

    return params;
};

const fetchDataSilent = async () => {
    if (abortController.value !== null) {
        abortController.value.abort();
    }
    abortController.value = new AbortController();
    try {

        if (!shouldFetch.value) {
            tableData.value = {
                data: []
            };

            return;
        }

        let params = buildParams();

        emit('fetchingData', true);

        const response = await axios.get(`${props.url}?${params}`, { signal: abortController.value.signal });
        tableData.value = response.data;

        emit('pageLoaded', response.data);
    } catch (error) {
        if (error.code === 'ERR_CANCELED') {
            return;
        }
        await alert({
            message:
                error?.response?.data?.error ??
                error?.response?.data?.message ??
                'Invalid request. Try again later'
        });
    } finally {
        emit('fetchingData', false);
    }
}

const fetchData = async function() {
    if (abortController.value !== null) {
        abortController.value.abort();
    }
    abortController.value = new AbortController();
    try {

        if (!shouldFetch.value) {
            tableData.value = {
                data: []
            };

            return;
        }

        let params = buildParams();

        if (true === isCsvExport.value) {
            params.append("csv", "true");

            const response = await axios.get(`${props.url}?${params}`, { signal: abortController.value.signal });

            const blob = new Blob([response.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');

            const contentDisposition = response.headers.get('Content-Disposition');

            let filename;
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }

            if (!filename) {
                filename = `exported_table_${moment().format('YY-MM-DD')}.csv`;
            }

            a.href = url;
            a.download = filename;

            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
        } else {
            emit('fetchingData', true);

            isLoading.value = true;
            const response = await axios.get(`${props.url}?${params}`, { signal: abortController.value.signal });
            tableData.value = response.data;

            emit('pageLoaded', response.data);
        }
    } catch (error) {
        if (error.code === 'ERR_CANCELED') {
            return;
        }
        if (props.displayErrorAlert) {
            await alert({
                message:
                    error?.response?.data?.error ??
                    error?.response?.data?.message ??
                    "Invalid request. Try again later"
            });
        }
        emit('serverError', error);
    } finally {
        isLoading.value = false;
        isCsvExport.value = false;

        emit('fetchingData', false);
    }
};

const performSearch = async function() {
    if (isLoading.value) {
        return;
    }

    page.value = 1;

    await fetchData();
};

const changePage = async function(newPage = 1) {
    if (isLoading.value) {
        return;
    }

    page.value = newPage;

    await fetchData();
};

const changeOrder = async ($event, column) => {
    if (false === column.sortable) {
        return;
    }

    if (isLoading.value) {
        return;
    }

    if ($event.target.classList.contains('data-table-header-filter')) {
        return;
    }

    if ($event.target.classList.contains('input-filter-icon')) {
        return;
    }

    page.value = 1;

    // Increment the click count for the clicked column
    clickCounts.value[column.name] = (clickCounts.value[column.name] || 0) + 1;

    // Determine the order direction based on the click count
    if (clickCounts.value[column.name] === 1) {
        orderDir.value = "asc";
        orderBy.value = column.name;
    } else if (clickCounts.value[column.name] === 2) {
        orderDir.value = "desc";
        orderBy.value = column.name;
    } else {
        // Reset the click count and order direction
        clickCounts.value[column.name] = 0;
        orderDir.value = null;
        orderBy.value = null;
    }

    emit("update:order", orderBy.value, orderDir.value)

    await fetchData();
};

const updatePageLength = async function(value) {
    if (isLoading.value) {
        return;
    }

    page.value = 1;
    pageLength.value = value;

    await fetchData();
};

const updateColumns = function() {
    const newColumns = columns.value.slice();

    newColumns.forEach(col => {
        col.name = col.name || col.data;
        col.title = col.title !== undefined ? col.title : startCase(col.name.toLowerCase());
        col.icon = col.icon !== undefined ? col.icon : null;
        col.info = col.info !== undefined ? col.info : null;
        col.tooltip = col.tooltip !== undefined ? col.tooltip : null;
        col.html = col.html !== undefined ? col.html : null;
        col.width = col.width !== undefined ? col.width : null;
        col.checkbox = col.checkbox !== undefined ? col.checkbox : false;
        col.sortable = col.sortable !== undefined ? col.sortable : true;
        col.visible = col.visible !== undefined ? col.visible : true;
        col.columnSelector = col.columnSelector !== undefined ? col.columnSelector : true;
        col.openModal = col.openModal !== undefined ? col.openModal : null;
        col.inputFilter = col.inputFilter !== undefined ? col.inputFilter : null;
        col.onClick = col.onClick !== undefined ? col.onClick : null;
        col.className = col.className !== undefined ? col.className : null;
    });

    columns.value = newColumns;
};

updateColumns();

const updateTableData = (newData) => tableData.value = newData;

const clearInputFilter = (col) => {
    col.inputFilter.defaultValue = '';
}

watch([page, pageLength, orderBy, orderDir, searchTerm], function() {
    if (!props.useLocalStorage) {
        return;
    }

    localStorage.setItem(idLocalStorage, JSON.stringify({
        page: page.value,
        pageLength: pageLength.value,
        orderBy: orderBy.value,
        orderDir: orderDir.value,
        searchTerm: searchTerm.value
    }));
}, {
    deep: true
});

watch(() => props.orderBy, (newValue) => {
    orderBy.value = newValue;
});

watch(() => props.orderDir, (newValue) => {
    orderDir.value = newValue || "asc";
});

watch(() => props.filters, async () => {
    page.value = 1;

    // Call the fetchData function when the appliedFilters change
    await fetchData();
}, { deep: true });

watch(() => props.columns, (newValue) => {
    columns.value = newValue;

    updateColumns();
});

watch(() => props.refresh, async (newValue) => {
    if (newValue) {
        emit('refreshTableData');
        await fetchData();
    }
});

watch(() => props.refreshSilent, async (newValue) => {
    if (newValue) {
        await fetchDataSilent();
    }
});

watch(selectAllChecked, (newValue) => {
    emit('selectAllChange', newValue);
});

onMounted(async () => {
    const savedData = JSON.parse(localStorage.getItem(idLocalStorage)) || {};

    page.value = savedData.page || page.value;
    pageLength.value = savedData.pageLength || pageLength.value;
    orderBy.value = savedData.orderBy || orderBy.value;
    orderDir.value = savedData.orderDir || orderDir.value;
    searchTerm.value = savedData.searchTerm || searchTerm.value;
    clickCounts.value[orderBy.value] = orderDir.value === "asc" ? 1 : 2;

    if (!props.lazyLoad) {
        await fetchData();
    }

    [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]')).map(
        el => new bootstrap.Tooltip(el, { trigger: "hover" })
    );
});

defineExpose({
    page,
    changePage,
    fetchData,
    updateTableData
})
watch(() => tableData.value.data, (val) => emit('update:modelValue', val));
</script>

<style scoped>

.table-responsive {
    max-height: 400px;
    overflow: auto;
    scrollbar-color: #bbbbbb #ffffff;
    scrollbar-width: thin;
}

.table-responsive::-webkit-scrollbar {
    width: 8px;
}

.table-responsive::-webkit-scrollbar:horizontal {
    width: 8px;
}

.table-responsive::-webkit-scrollbar-thumb {
    background-color: #bbbbbb;
    border-radius: 6px;
}

.table-responsive::-webkit-scrollbar-thumb:hover {
    background-color: #777777;
}

.sticky-header {
    position: sticky;
    top: 0;
    z-index: 1;
}

.loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: rgba(255, 255, 255, 0.2);
    z-index: 10;
}

th.sortable {
    cursor: pointer;
}

th.sortable.sorting-asc {
    color: var(--bs-primary);
}

th.sortable.sorting-desc {
    color: var(--bs-primary);
}

th.sortable.sorting-asc i {
    font-weight: bolder;
}

th.sortable.sorting-desc i {
    font-weight: bolder;
}

.datatable-search {
    width: 400px;
}

.fixed-block {
    position: absolute;
    left: -172px;
    top: 40px;
}

.header-filter-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.clickable-row {
    cursor: pointer;
}
</style>
